import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { isSupportedISP } from '../../../../../models/UiUtils';
import {
	AccountOperationsViewModel,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../../web/components-v2/Button';
import { Input } from '../../../../../web/components-v2/Input';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { DangerMessage } from '../../../../../web/components/errorMessages/DangerMessage';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../web/components/Select';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { AutoDiscoverEmailModal } from '../../../../components/tools/AutoDiscoverEmailModal';
import { styleSheet } from './styles';

export interface IEmailConfigurationWithKey extends Api.IEmailProviderConfiguration {
	key: string;
	defaultEmailSendLimits?: Api.IEmailSendLimit[];
	defaultSecondsBetweenSends?: number;
}

interface IProps {
	accountOperations: AccountOperationsViewModel;
	configuration: IEmailConfigurationWithKey;
	disabled?: boolean;
	onDelete?(configuration: IEmailConfigurationWithKey): void;
	onUpdate(configuration: IEmailConfigurationWithKey): void;
	userSession: AdminUserSessionContext;
}

interface IState {
	autoDiscoverModalIsOpen?: boolean;
	imapISP?: string;
	useLevDomain: boolean;
}

interface IImapProvider {
	configuration: Api.IImapEmailConfiguration;
	displayName: string;
}

const useLevDomainOptions = [
	'independentagent.email',
	'securegateway.email',
	'myagent.email',
	'investmentadvisor.email',
];

const emailProviderOptions = [
	{ value: 'Google', label: 'Google Workspace (for business)' },
	{ value: 'Office365', label: 'Office 365' },
	{ value: 'Outlook', label: 'Outlook.com (Personal)' },
	{ value: 'Gmail', label: 'Gmail' },
	{ value: 'Exchange', label: 'Exchange' },
	{ value: 'Imap', label: 'IMAP' },
	{ value: 'Yahoo', label: 'Yahoo' },
	{ value: 'Aol', label: 'AOL' },
];

const imapProviders: IImapProvider[] = [
	{
		configuration: {
			imapHost: 'secure.emailsrvr.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'secure.emailsrvr.com',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Rackspace',
	},
	{
		configuration: {
			imapHost: 'imap.secureserver.net',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtpout.secureserver.net',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'GoDaddy',
	},
	{
		configuration: {
			imapHost: 'imap.1and1.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.1and1.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: '1and1',
	},
	{
		configuration: {
			imapHost: 'imap.fusemail.net',
			imapPort: 143,
			imapUseSsl: false,
			smtpHost: 'smtp.fusemail.net',
			smtpPort: 2500,
			smtpUseSsl: false,
		},
		displayName: 'FuseMail',
	},
	{
		configuration: {
			imapHost: 'imap.mail.yahoo.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.mail.yahoo.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: 'Yahoo',
	},
	{
		configuration: {
			imapHost: 'imap.aol.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.aol.com',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Aol',
	},
	{
		configuration: {
			imapHost: 'imap.mail.me.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.mail.me.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: 'iCloud',
	},
	{
		configuration: {
			imapHost: 'mail.smarshmail.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'mail.smarshmail.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: 'Smarshmail',
	},
	{
		configuration: {
			imapHost: 'imap.redtailtechnology.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.redtailtechnology.com',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Redtail',
	},
	{
		configuration: {
			imapHost: '',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: '',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Other',
	},
];

class _EmailConfiguration extends React.Component<IProps, IState> {
	private get dailyLimit(): number {
		return this.getEmailSendLimit(1440, this.props.configuration.emailSendLimits);
	}

	private get defaultDailyLimit(): number {
		return this.getEmailSendLimit(1440, this.props.configuration.defaultEmailSendLimits);
	}

	private get defaultHourlyLimit(): number {
		return this.getEmailSendLimit(60, this.props.configuration.defaultEmailSendLimits);
	}

	private get hourlyLimit(): number {
		return this.getEmailSendLimit(60, this.props.configuration.emailSendLimits);
	}

	constructor(props: IProps) {
		super(props);

		this.state = {
			imapISP: 'Other',

			useLevDomain: !!props.configuration?.sendFrom?.domain || !isSupportedISP(props.configuration.domain),
		};
	}

	public render() {
		const { configuration, disabled, onDelete, userSession } = this.props;
		const { autoDiscoverModalIsOpen, useLevDomain } = this.state;
		const { defaultSecondsBetweenSends, domain, emailProvider } = configuration;

		const haveSupportLevel3 = userSession.hasPermission(AdminPermission.SupportLevel3);
		let candidateDomains = useLevDomainOptions;

		if (haveSupportLevel3 && domain) {
			if (!candidateDomains.find(d => d === domain)) {
				candidateDomains = [domain];
			}
		}

		const configuredSendEmailDomain = this.props.accountOperations.account.features?.sendEmail?.domainName;
		if (domain && configuredSendEmailDomain) {
			candidateDomains = [configuredSendEmailDomain, ...candidateDomains];
		}

		return (
			<div className={`${css(styleSheet.container)} email-configuration`}>
				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.header)}>Domain</div>
					<div className={css(styleSheet.fields)}>
						<Input
							id='domain'
							disabled={disabled}
							onChange={this.updateFormFieldValue}
							placeholder='Domain'
							value={domain || ''}
							styles={[styleSheet.hostName]}
						/>
						<Select
							disabled={disabled}
							value={emailProvider || ''}
							onValueChange={value =>
								this.updateSelectFieldValue({
									target: { id: 'accountEmailProvider', value },
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							items={emailProviderOptions}
						>
							<SelectTrigger id='accountEmailProvider' styles={styleSheet.useLevSelect}>
								<SelectValue placeholder='-- select provider --' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Google'>Google Workspace (for business)</SelectItem>
								<SelectItem value='Office365'>Office 365</SelectItem>
								<SelectItem value='Outlook'>Outlook.com (Personal)</SelectItem>
								<SelectItem value='Gmail'>Gmail</SelectItem>
								<SelectItem value='Exchange'>Exchange</SelectItem>
								<SelectItem value='Imap'>IMAP</SelectItem>
								<SelectItem value='Yahoo'>Yahoo</SelectItem>
								<SelectItem value='Aol'>AOL</SelectItem>
							</SelectContent>
						</Select>
						{!!onDelete && !disabled && (
							<Button variant='destructive' size='sm' onClick={this.onDelete}>
								Delete
							</Button>
						)}
					</div>

					{!isSupportedISP(domain) && (
						<DangerMessage styles={[styleSheet.unsupportedISP]}>
							This email domain is a consumer ISP, and we cannot send group emails for this email domain. The customer
							needs to be provisioned a dedicated email domain. Please open a support ticket or GitHub to address.
						</DangerMessage>
					)}
				</div>
				<div className={css(styleSheet.section)}>
					<Checkbox
						className={css(styleSheet.useLevCheckbox)}
						id={`send-from-lev-domain-${configuration.key}`}
						type='large'
						onChange={this.onUseLevDomainChecked}
						checked={useLevDomain}
						disabled={!isSupportedISP(configuration.domain) || !haveSupportLevel3}
					>
						<span className={css(styleSheet.useLevText)}>Use Levitate Domain to send group emails</span>
					</Checkbox>
				</div>
				{useLevDomain && (
					<div className={css(styleSheet.section)}>
						<Select
							disabled={!haveSupportLevel3}
							value={configuration?.sendFrom?.domain}
							onValueChange={this.onLevDomainChanged}
						>
							<SelectTrigger styles={styleSheet.useLevSelect}>
								<SelectValue placeholder='Please select domain' />
							</SelectTrigger>
							<SelectContent>
								{candidateDomains.map(candidateDomain => (
									<SelectItem key={candidateDomain} value={candidateDomain}>
										{candidateDomain}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
				<div className={css(styleSheet.section, styleSheet.sendOptions)}>
					<div>
						<div className={css(styleSheet.header)}>Custom Send Limits</div>
						<div className={css(styleSheet.fields)}>
							<div>
								<label className={css(styleSheet.label)}>Daily</label>
								<Input
									id='dailyLimit'
									styles={[styleSheet.sendLimit]}
									onChange={this.updateFormFieldValue}
									placeholder={`${this.defaultDailyLimit ?? ''}`}
									value={this.dailyLimit ?? ''}
								/>
							</div>
							<div>
								<label className={css(styleSheet.label)}>Hourly</label>
								<Input
									id='hourlyLimit'
									styles={[styleSheet.sendLimit]}
									onChange={this.updateFormFieldValue}
									placeholder={`${this.defaultHourlyLimit ?? '0'}`}
									value={this.hourlyLimit ?? ''}
								/>
							</div>
							<div>
								<label className={css(styleSheet.label)}>Seconds Between Sends</label>
								<Input
									id='betweenSends'
									onChange={this.updateFormFieldValue}
									placeholder={`${defaultSecondsBetweenSends ?? '0'}`}
									value={configuration.secondsBetweenSends ?? ''}
								/>
							</div>
						</div>
					</div>
				</div>
				{this.renderExchange()}
				{this.renderImap()}
				<button
					className={css(baseStyleSheet.brandLink, styleSheet.autoDiscoverButton)}
					onClick={this.onOpenAutoDiscoverModal}
				>
					Auto Discover Configuration
				</button>
				{autoDiscoverModalIsOpen && (
					<AutoDiscoverEmailModal
						initialDomain={domain}
						onRequestClose={this.onCloseAutoDiscoverModal}
						showUseButton={true}
					/>
				)}
			</div>
		);
	}

	private onLevDomainChanged = (domain: string) => {
		const configuration: IEmailConfigurationWithKey = {
			...this.props.configuration,
			sendFrom: { domain },
		};

		this.props.onUpdate(configuration);
	};

	private onUseLevDomainChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
		const configuration: IEmailConfigurationWithKey = {
			...this.props.configuration,

			sendFrom: null,
		};

		this.props.onUpdate(configuration);

		this.setState({ useLevDomain: e.target.checked });
	};

	private onDelete = () => {
		this.props.onDelete(this.props.configuration);
	};

	private renderExchange() {
		const { disabled, configuration } = this.props;
		const { emailProvider, exchange } = configuration;
		if (emailProvider !== 'Exchange') {
			return null;
		}

		return (
			<div className={css(styleSheet.section)}>
				<div title='If the customer accesses "https://mail.mydomain.com" on the web, their Exchange url will be "https://mail.mydomain.com/ews/Exchange.asmx"'>
					<div className={css(styleSheet.header)}>Exchange Details (?)</div>
					<div className={css(styleSheet.fields)}>
						<input
							className={css(styleSheet.textBox, styleSheet.exchangeUrl)}
							disabled={disabled}
							id='exchangeWebServicesUrl'
							onChange={this.updateFormFieldValue}
							placeholder='Service Url (ends with /ews/Exchange.asmx)'
							type='text'
							value={exchange ? exchange.webServicesUrl : ''}
						/>
					</div>
				</div>

				{this.renderCertificateOptions(exchange, 'exchange')}
			</div>
		);
	}

	private renderImap() {
		const { disabled, configuration } = this.props;
		const { emailProvider, imap } = configuration;
		if (emailProvider !== 'Imap') {
			return null;
		}

		const isSmtpOnly = imap.imapHost === 'SMTPONLY';

		const imapHost = imap.imapHost;

		const imapPort = imap.imapPort ? imap.imapPort.toString() : '';

		const smtpHost = imap.smtpHost;

		const smtpPort = imap.smtpPort ? imap.smtpPort.toString() : '';

		return (
			<div>
				{!isSmtpOnly && (
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.header)}>ISP</div>
						<div className={css(styleSheet.fields)}>
							<select
								id='imapISP'
								className={css(styleSheet.dropDown)}
								value={this.state.imapISP || 'Other'}
								onChange={this.updateSelectFieldValue}
								disabled={disabled}
							>
								{imapProviders.map(provider => {
									return (
										<option key={provider.displayName} value={provider.displayName}>
											{provider.displayName}
										</option>
									);
								})}
							</select>
						</div>
					</div>
				)}
				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.header)}>IMAP Configuration</div>
					{isSmtpOnly ? (
						<div className={css(styleSheet.smtpOnlyWarning)}>
							IMAP is disabled, only email send will work. Email bounce and reply tracking will not be supported. Having
							the client upgrade to IMAP is the preferred solution.
						</div>
					) : (
						<div className={css(styleSheet.fields)}>
							<input
								type='text'
								id='imapHost'
								placeholder='IMAP Host'
								className={css(styleSheet.textBox, styleSheet.hostName)}
								value={imapHost}
								onChange={this.updateFormFieldValue}
								disabled={disabled}
							/>
							<input
								type='text'
								id='imapPort'
								placeholder='IMAP Port'
								value={imapPort}
								className={css(styleSheet.textBox, styleSheet.portNumber)}
								onChange={this.updateFormFieldValue}
								disabled={disabled}
							/>
							<div className={css(styleSheet.checkbox)}>
								<input id='imapSsl' type='checkbox' checked={imap.imapUseSsl} onChange={this.updateFormFieldValue} />
								<span>Use SSL</span>
							</div>
						</div>
					)}
				</div>
				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.header)}>SMTP Configuration</div>
					<div className={css(styleSheet.fields)}>
						<input
							autoComplete='off'
							type='text'
							id='smtpHost'
							placeholder='SMTP Host'
							className={css(styleSheet.textBox, styleSheet.hostName)}
							value={smtpHost}
							onChange={this.updateFormFieldValue}
							disabled={disabled}
						/>
						<input
							type='text'
							id='smtpPort'
							placeholder='SMTP Port'
							className={css(styleSheet.textBox, styleSheet.portNumber)}
							value={smtpPort}
							onChange={this.updateFormFieldValue}
							disabled={disabled}
						/>
						<div className={css(styleSheet.checkbox)}>
							<input
								id='smtpSsl'
								type='checkbox'
								checked={imap.smtpUseSsl}
								onChange={this.updateFormFieldValue}
								disabled={disabled}
							/>
							<span>Use SSL</span>
						</div>
					</div>
					<div className={css(styleSheet.smtpOnly)}>
						<div className={css(styleSheet.checkbox)}>
							<input id='smtpOnly' type='checkbox' checked={isSmtpOnly} onChange={this.onToggleSmtpOnly} />
							<span>SMTP Only</span>
						</div>
					</div>
				</div>

				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.header)}>Network Proxy Endpoint</div>
					<div className={css(styleSheet.fields)}>
						<select
							id='imapProxyType'
							className={css(styleSheet.dropDown)}
							value={imap.proxy ? imap.proxy.endpoint : Api.NetworkProxyType.None}
							onChange={this.updateSelectFieldValue}
							disabled={disabled}
						>
							<option value='None'>None</option>
							<option value='Azure'>Azure</option>
							<option value='DigitalOcean'>DigitalOcean</option>
							<option value='Linode'>Linode</option>
						</select>
					</div>
				</div>

				{this.renderCertificateOptions(imap, 'imap')}
			</div>
		);
	}

	private renderCertificateOptions(options: Api.IEmailCertificateOptions, providerType: 'imap' | 'exchange') {
		const { disabled } = this.props;

		return (
			<div className={css(styleSheet.section)}>
				<div className={css(styleSheet.header)}>Certificates</div>
				<div className={css(styleSheet.fields)}>
					<div className={css(styleSheet.checkbox)} title='Should only be used if issue is reported.'>
						<input
							id='allowAnyValidCertificate'
							type='checkbox'
							checked={options.allowAnyValidCertificate}
							onChange={this.updateCertificateOptions(options, providerType)}
							disabled={disabled}
						/>
						<span>Allow any valid certificate</span>
					</div>
				</div>
				<div className={css(styleSheet.fields)}>
					<div className={css(styleSheet.checkbox)} title='Should only be used if issue is reported.'>
						<input
							id='allowAnyCertificate'
							type='checkbox'
							checked={options.allowAnyCertificate}
							onChange={this.updateCertificateOptions(options, providerType)}
							disabled={disabled}
						/>
						<span>Allow any certificate</span>
					</div>
				</div>
			</div>
		);
	}

	private updateCertificateOptions =
		(certificateOptions: Api.IEmailCertificateOptions, providerType: 'imap' | 'exchange') =>
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const elementId = e.target.id;
			const { emailProvider, domain } = this.props.configuration;
			let { imap, exchange } = this.props.configuration;

			switch (elementId) {
				case 'allowAnyValidCertificate':
					certificateOptions.allowAnyValidCertificate = !!e.target.checked;
					break;
				case 'allowAnyCertificate':
					certificateOptions.allowAnyCertificate = !!e.target.checked;
					break;
				default:
					break;
			}

			switch (providerType) {
				case 'imap':
					imap = {
						...imap,
						...certificateOptions,
					};

					break;
				case 'exchange':
					exchange = {
						...exchange,
						...certificateOptions,
					};

					break;

				default:
					break;
			}

			const configuration = {
				...this.props.configuration,
				domain,
				emailProvider,
				exchange,
				imap,
			};

			this.props.onUpdate(configuration);
		};

	private updateFormFieldValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		const elementId = e.target.id;
		const elementValue = e.target.value;

		let { imap, exchange, emailProvider, domain, emailSendLimits, secondsBetweenSends, sendFrom } =
			this.props.configuration;

		switch (elementId) {
			case 'accountEmailProvider':
				emailSendLimits = this.setEmailSendLimit([], 0, '0');
				emailProvider = elementValue as Api.EmailProviderType;
				break;
			case 'domain':
				if (!isSupportedISP(elementValue)) {
					this.setState({ useLevDomain: true });
					sendFrom = { domain: 'myagent.email' };
				}
				domain = elementValue;
				break;
			case 'imapHost':
				imap.imapHost = elementValue;
				break;
			case 'imapPort':
				imap.imapPort = elementValue === '0' ? 0 : parseInt(elementValue, 10) || null;
				break;
			case 'imapSsl':
				imap.imapUseSsl = !!e.target.checked;
				break;
			case 'smtpHost':
				imap.smtpHost = elementValue;
				break;
			case 'smtpPort':
				imap.smtpPort = elementValue === '0' ? 0 : parseInt(elementValue, 10) || null;
				break;
			case 'smtpSsl':
				imap.smtpUseSsl = !!e.target.checked;
				break;
			case 'exchangeWebServicesUrl':
				exchange.webServicesUrl = e.target.value;
				break;
			case 'hourlyLimit':
				emailSendLimits = this.setEmailSendLimit(emailSendLimits, 60, e.target.value);
				break;
			case 'dailyLimit':
				emailSendLimits = this.setEmailSendLimit(emailSendLimits, 1440, e.target.value);
				break;
			case 'betweenSends':
				secondsBetweenSends = elementValue === '0' ? 0 : parseInt(elementValue, 10) || null;
				break;
			default:
				break;
		}

		const configuration = {
			...this.props.configuration,
			domain,
			emailProvider,
			emailSendLimits,
			exchange,
			imap,
			secondsBetweenSends,
			sendFrom,
		};

		this.props.onUpdate(configuration);
	};

	private getEmailSendLimit(intervalInMinutes: number, emailSendLimits: Api.IEmailSendLimit[]) {
		if (emailSendLimits) {
			const limit = emailSendLimits.find(x => x.intervalInMinutes === intervalInMinutes);
			return limit ? limit.count : undefined;
		}

		return null;
	}

	private setEmailSendLimit(
		emailSendLimits: Api.IEmailSendLimit[],
		intervalInMinutes: number,
		countStr: string
	): Api.IEmailSendLimit[] {
		const count = parseInt(countStr, 10);

		if (!emailSendLimits || emailSendLimits.length === 0) {
			return count >= 0 ? [{ count, intervalInMinutes }] : [];
		}

		emailSendLimits = emailSendLimits.filter(x => x.intervalInMinutes !== intervalInMinutes);
		if (count >= 0) {
			emailSendLimits.push({ count, intervalInMinutes });
		}

		return emailSendLimits;
	}

	private updateSelectFieldValue = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const elementId = e.target.id;
		const elementValue = e.target.value;
		let imapISP = this.state.imapISP;

		let { imap, emailProvider, emailSendLimits } = this.props.configuration;

		switch (elementId) {
			case 'accountEmailProvider': {
				emailSendLimits = this.setEmailSendLimit([], 0, '0');
				emailProvider = elementValue as Api.EmailProviderType;

				if (emailProvider === 'Yahoo' || emailProvider === 'Aol') {
					const provider = imapProviders.find(p => p.displayName === emailProvider);
					if (provider) {
						imap = {
							...imap,
							...provider.configuration,
						};
					}
				}
				break;
			}
			case 'imapISP': {
				imapISP = elementValue;

				const mappedProvider = imapProviders.find(p => p.displayName === imapISP);
				if (mappedProvider) {
					imap = {
						...imap,
						...mappedProvider.configuration,
					};
				}
				break;
			}
			case 'imapProxyType': {
				const endpoint = elementValue as Api.NetworkProxyEndpoint;
				switch (endpoint) {
					case Api.NetworkProxyEndpoint.Azure:
					case Api.NetworkProxyEndpoint.DigitalOcean:
					case Api.NetworkProxyEndpoint.Linode:
						imap.proxy = {
							endpoint,
							type: Api.NetworkProxyType.Socks5,
						};
						break;
					default:
						imap.proxy = {
							endpoint: Api.NetworkProxyEndpoint.None,
							type: Api.NetworkProxyType.None,
						};
						break;
				}
				break;
			}
			default:
				break;
		}

		const configuration = {
			...this.props.configuration,
			emailProvider,
			emailSendLimits,
			imap,
		};

		this.props.onUpdate(configuration);

		this.setState({
			imapISP,
		});
	};

	private onToggleSmtpOnly = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { configuration, onUpdate } = this.props;
		if (e.target.checked) {
			onUpdate({
				...configuration,
				imap: { ...configuration.imap, imapHost: 'SMTPONLY' },
			});
		} else {
			onUpdate({
				...configuration,
				imap: { ...configuration.imap, imapHost: '' },
			});
		}
	};

	private onOpenAutoDiscoverModal = () => {
		this.setState({ autoDiscoverModalIsOpen: true });
	};

	private onCloseAutoDiscoverModal = (autoConfig?: Api.IEmailProviderConfiguration) => {
		const { configuration, onUpdate } = this.props;
		this.setState({ autoDiscoverModalIsOpen: false });
		if (autoConfig?.domain) {
			onUpdate({ ...configuration, ...autoConfig });
		}
	};
}

export const EmailConfiguration = observer(_EmailConfiguration);
